import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Socket } from 'socket.io-client';
import { io } from 'socket.io-client';
import { getSocketServerUrl } from '@lighthouse/shared';
import { useAdminAuth } from '../hooks/useAdminAuth';
import {
  API_URL,
  adminApi,
  ensureArray,
  getAdminAccessToken,
  refreshAdminToken,
} from '../services/api';

interface StaffMember {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  adminStaffRole: string | null;
  online: boolean;
}

interface ConversationSummary {
  id: string;
  otherParticipant: {
    id: string;
    email: string;
    firstName: string | null;
    lastName: string | null;
    online: boolean;
  };
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
}

interface StaffMessage {
  id: string;
  conversationId: string;
  senderId: string;
  content: string;
  createdAt: string;
}

function displayName(person: {
  email: string;
  firstName: string | null;
  lastName: string | null;
}): string {
  const name = [person.firstName, person.lastName].filter(Boolean).join(' ').trim();
  return name || person.email;
}

function initials(person: {
  email: string;
  firstName: string | null;
  lastName: string | null;
}): string {
  if (person.firstName) {
    return `${person.firstName[0] ?? ''}${person.lastName?.[0] ?? ''}`.toUpperCase() || '?';
  }
  return (person.email[0] ?? '?').toUpperCase();
}

function roleLabel(role: string | null): string {
  if (!role) return 'Staff';
  return role
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function StaffChatPage() {
  const { user } = useAdminAuth();
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [onlineIds, setOnlineIds] = useState<Set<string>>(new Set());
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [activeConversationId, setActiveConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<StaffMessage[]>([]);
  const [input, setInput] = useState('');
  const [connected, setConnected] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [composingNew, setComposingNew] = useState(false);

  const socketRef = useRef<Socket | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const typingTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const typing = useRef(false);
  const activeConversationIdRef = useRef<string | null>(null);

  useEffect(() => {
    activeConversationIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const loadLists = useCallback(async () => {
    setError('');
    try {
      const [staffRes, convRes] = await Promise.all([
        adminApi<{ data: StaffMember[] }>('/admin/staff-chat/staff'),
        adminApi<{ data: ConversationSummary[] }>('/admin/staff-chat/conversations'),
      ]);
      const staffList = ensureArray<StaffMember>(staffRes.data);
      setStaff(staffList);
      setConversations(ensureArray<ConversationSummary>(convRes.data));
      setOnlineIds(new Set(staffList.filter((s) => s.online).map((s) => s.id)));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadLists();
  }, [loadLists]);

  useEffect(() => {
    let cancelled = false;

    async function connect() {
      let token = getAdminAccessToken();
      if (!token) {
        token = await refreshAdminToken();
      }
      if (!token || cancelled) return;

      const socket: Socket = io(getSocketServerUrl(API_URL), {
        auth: { token },
        transports: ['websocket'],
      });
      socketRef.current = socket;

      socket.on('connect', () => setConnected(true));
      socket.on('disconnect', () => setConnected(false));
      socket.on('error', (e: { message?: string }) => {
        if (e?.message) setError(e.message);
      });
      socket.on('staff:presence:snapshot', (payload: { onlineIds: string[] }) => {
        setOnlineIds(new Set(payload.onlineIds ?? []));
      });
      socket.on('staff:presence:changed', (payload: { userId: string; online: boolean }) => {
        setOnlineIds((prev) => {
          const next = new Set(prev);
          if (payload.online) next.add(payload.userId);
          else next.delete(payload.userId);
          return next;
        });
        setStaff((prev) =>
          prev.map((s) => (s.id === payload.userId ? { ...s, online: payload.online } : s))
        );
        setConversations((prev) =>
          prev.map((c) =>
            c.otherParticipant.id === payload.userId
              ? {
                  ...c,
                  otherParticipant: { ...c.otherParticipant, online: payload.online },
                }
              : c
          )
        );
      });
      socket.on('staff:chat:history', (history: StaffMessage[]) => {
        setMessages(ensureArray(history));
      });
      socket.on('staff:chat:message', (msg: StaffMessage) => {
        setMessages((prev) => {
          if (prev.some((m) => m.id === msg.id)) return prev;
          if (
            activeConversationIdRef.current &&
            msg.conversationId !== activeConversationIdRef.current
          ) {
            return prev;
          }
          if (!activeConversationIdRef.current) return prev;
          return [...prev, msg];
        });
        setConversations((prev) => {
          const updated = prev.map((c) =>
            c.id === msg.conversationId
              ? {
                  ...c,
                  lastMessageAt: msg.createdAt,
                  lastMessagePreview:
                    msg.content.length > 80 ? `${msg.content.slice(0, 80)}…` : msg.content,
                }
              : c
          );
          return [...updated].sort((a, b) => {
            const aTime = a.lastMessageAt ? Date.parse(a.lastMessageAt) : 0;
            const bTime = b.lastMessageAt ? Date.parse(b.lastMessageAt) : 0;
            return bTime - aTime;
          });
        });
      });
      socket.on('staff:chat:typing', ({ isTyping }: { isTyping: boolean }) => {
        setOtherTyping(isTyping);
      });
    }

    void connect();

    return () => {
      cancelled = true;
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!activeConversationId || !socketRef.current?.connected) return;
    socketRef.current.emit('join:staff-conversation', activeConversationId);
    setOtherTyping(false);
  }, [activeConversationId, connected]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const filteredStaff = useMemo(() => {
    const q = search.trim().toLowerCase();
    return staff.filter((s) => {
      const isOnline = onlineIds.has(s.id);
      if (onlineOnly && !isOnline) return false;
      if (!q) return true;
      const hay =
        `${s.email} ${s.firstName ?? ''} ${s.lastName ?? ''} ${s.adminStaffRole ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [staff, search, onlineOnly, onlineIds]);

  const filteredConversations = useMemo(() => {
    const q = search.trim().toLowerCase();
    return conversations.filter((c) => {
      const isOnline = onlineIds.has(c.otherParticipant.id);
      if (onlineOnly && !isOnline) return false;
      if (!q) return true;
      const p = c.otherParticipant;
      const hay =
        `${p.email} ${p.firstName ?? ''} ${p.lastName ?? ''} ${c.lastMessagePreview ?? ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [conversations, search, onlineOnly, onlineIds]);

  const activeConversation = conversations.find((c) => c.id === activeConversationId) ?? null;
  const onlineCount = staff.filter((s) => onlineIds.has(s.id)).length;

  const openConversation = async (conversationId: string) => {
    setComposingNew(false);
    setActiveConversationId(conversationId);
    setMessages([]);
    try {
      const history = await adminApi<StaffMessage[]>(
        `/admin/staff-chat/conversations/${conversationId}/messages`
      );
      setMessages(ensureArray(history));
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const startChatWith = async (member: StaffMember) => {
    setError('');
    try {
      const existing = conversations.find((c) => c.otherParticipant.id === member.id);
      if (existing) {
        await openConversation(existing.id);
        return;
      }
      const created = await adminApi<{ id: string }>('/admin/staff-chat/conversations', {
        method: 'POST',
        body: JSON.stringify({ participantId: member.id }),
      });
      await loadLists();
      await openConversation(created.id);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const sendMessage = () => {
    const trimmed = input.trim();
    if (!trimmed || !activeConversationId || !socketRef.current) return;
    socketRef.current.emit('staff:chat:message', {
      conversationId: activeConversationId,
      content: trimmed,
    });
    setInput('');
    typing.current = false;
    socketRef.current.emit('staff:chat:typing', {
      conversationId: activeConversationId,
      isTyping: false,
    });
  };

  const handleTyping = (value: string) => {
    setInput(value);
    if (!activeConversationId || !socketRef.current) return;
    if (!typing.current) {
      typing.current = true;
      socketRef.current.emit('staff:chat:typing', {
        conversationId: activeConversationId,
        isTyping: true,
      });
    }
    clearTimeout(typingTimer.current);
    typingTimer.current = setTimeout(() => {
      typing.current = false;
      socketRef.current?.emit('staff:chat:typing', {
        conversationId: activeConversationId,
        isTyping: false,
      });
    }, 2000);
  };

  return (
    <div className="staff-chat flex h-[calc(100vh-2.5rem)] min-h-[480px] overflow-hidden lg:h-[calc(100vh-3rem)]">
      <aside className="glass flex w-full max-w-sm flex-col border-r border-white/50">
        <div className="border-b border-white/50 px-4 py-3">
          <div className="mb-3 flex items-center justify-between gap-2">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Staff Chat</h2>
              <p className="text-xs text-gray-500">
                {onlineCount} online · {connected ? 'Live' : 'Reconnecting…'}
              </p>
            </div>
            <button
              type="button"
              className="btn-primary !px-3 !py-1.5 text-xs"
              onClick={() => {
                setComposingNew(true);
                setActiveConversationId(null);
                setMessages([]);
              }}
            >
              New
            </button>
          </div>
          <input
            type="search"
            className="input-field mb-2"
            placeholder="Search messages"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search staff or conversations"
          />
          <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="text-primary-600 focus:ring-primary-500 rounded border-gray-300"
              checked={onlineOnly}
              onChange={(e) => setOnlineOnly(e.target.checked)}
            />
            Online only
          </label>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <p className="px-4 py-6 text-sm text-gray-500">Loading…</p>
          ) : composingNew ? (
            <div>
              <div className="border-b border-white/40 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Start a conversation
              </div>
              {filteredStaff.length === 0 ? (
                <p className="px-4 py-6 text-sm text-gray-500">
                  {onlineOnly ? 'No staff online right now.' : 'No matching staff.'}
                </p>
              ) : (
                filteredStaff.map((member) => {
                  const isOnline = onlineIds.has(member.id);
                  return (
                    <button
                      key={member.id}
                      type="button"
                      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/50"
                      onClick={() => void startChatWith(member)}
                    >
                      <div className="relative">
                        <div className="bg-primary-100 text-primary-800 flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold">
                          {initials(member)}
                        </div>
                        <span
                          className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white ${isOnline ? 'bg-emerald-500' : 'bg-gray-300'}`}
                          aria-label={isOnline ? 'Online' : 'Offline'}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-gray-900">
                          {displayName(member)}
                        </p>
                        <p className="truncate text-xs text-gray-500">
                          {roleLabel(member.adminStaffRole)}
                          {isOnline ? ' · Online' : ''}
                        </p>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm font-medium text-gray-700">No conversations yet</p>
              <p className="mt-1 text-xs text-gray-500">
                Click New to message a staff member
                {onlineOnly ? ' who is online' : ''}.
              </p>
            </div>
          ) : (
            filteredConversations.map((conv) => {
              const isOnline = onlineIds.has(conv.otherParticipant.id);
              const isActive = conv.id === activeConversationId;
              return (
                <button
                  key={conv.id}
                  type="button"
                  className={`flex w-full items-center gap-3 px-4 py-3 text-left transition-colors ${isActive ? 'bg-primary-50/80' : 'hover:bg-white/50'}`}
                  onClick={() => void openConversation(conv.id)}
                >
                  <div className="relative">
                    <div className="bg-primary-100 text-primary-800 flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold">
                      {initials(conv.otherParticipant)}
                    </div>
                    <span
                      className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white ${isOnline ? 'bg-emerald-500' : 'bg-gray-300'}`}
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-sm font-medium text-gray-900">
                        {displayName(conv.otherParticipant)}
                      </p>
                      {conv.lastMessageAt && (
                        <span className="shrink-0 text-[10px] text-gray-400">
                          {new Date(conv.lastMessageAt).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </span>
                      )}
                    </div>
                    <p className="truncate text-xs text-gray-500">
                      {conv.lastMessagePreview ?? (isOnline ? 'Online' : 'Offline')}
                    </p>
                  </div>
                </button>
              );
            })
          )}
        </div>
      </aside>

      <section className="glass flex min-w-0 flex-1 flex-col">
        {!activeConversationId || !activeConversation ? (
          <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
            <p className="text-base font-medium text-gray-700">Select a conversation…</p>
            <p className="mt-1 max-w-sm text-sm text-gray-500">
              Message other admins in real time. Use Online only to see who is available now.
            </p>
          </div>
        ) : (
          <>
            <header className="flex items-center gap-3 border-b border-white/50 px-4 py-3">
              <div className="relative">
                <div className="bg-primary-100 text-primary-800 flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold">
                  {initials(activeConversation.otherParticipant)}
                </div>
                <span
                  className={`absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full border-2 border-white ${onlineIds.has(activeConversation.otherParticipant.id) ? 'bg-emerald-500' : 'bg-gray-300'}`}
                />
              </div>
              <div className="min-w-0">
                <p className="truncate font-medium text-gray-900">
                  {displayName(activeConversation.otherParticipant)}
                </p>
                <p className="text-xs text-gray-500">
                  {onlineIds.has(activeConversation.otherParticipant.id) ? 'Online' : 'Offline'}
                  {otherTyping ? ' · typing…' : ''}
                </p>
              </div>
            </header>

            <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.length === 0 && (
                <p className="py-12 text-center text-sm text-gray-500">
                  No messages yet. Say hello.
                </p>
              )}
              {messages.map((msg) => {
                const isMine = msg.senderId === user?.id;
                return (
                  <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                    <div
                      className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm ${
                        isMine
                          ? 'bg-primary-600 rounded-br-sm text-white'
                          : 'rounded-bl-sm border border-white/80 bg-white/80 text-gray-900'
                      }`}
                    >
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      <p
                        className={`mt-1 text-[10px] ${isMine ? 'text-white/70' : 'text-gray-400'}`}
                      >
                        {new Date(msg.createdAt).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>

            <div className="border-t border-white/50 p-3">
              {error && <p className="mb-2 text-xs text-red-600">{error}</p>}
              <div className="flex gap-2">
                <input
                  className="input-field flex-1"
                  placeholder="Write a message…"
                  value={input}
                  onChange={(e) => handleTyping(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                  disabled={!connected}
                />
                <button
                  type="button"
                  className="btn-primary"
                  onClick={sendMessage}
                  disabled={!connected || !input.trim()}
                >
                  Send
                </button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
